export const CHECKOUT_SECTION_ID = 'checkout';

type CheckoutDocument = {
  getElementById(id: string): { scrollIntoView(options: ScrollIntoViewOptions): void } | null;
};

type LocationLike = {
  pathname: string;
  search: string;
  hash: string;
};

type HistoryLike = {
  replaceState(data: unknown, unused: string, url?: string | URL | null): void;
};

export function scrollToCheckoutSection(checkoutDocument: CheckoutDocument = document): boolean {
  const checkoutElement = checkoutDocument.getElementById(CHECKOUT_SECTION_ID);
  if (!checkoutElement) return false;
  checkoutElement.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
  return true;
}

export function handleCheckoutCtaClick(input: { checkoutDocument?: CheckoutDocument; track: () => void }): boolean {
  input.track();
  return scrollToCheckoutSection(input.checkoutDocument);
}

export function removeCheckoutFragment(location: LocationLike = window.location, history: HistoryLike = window.history): boolean {
  if (location.hash !== `#${CHECKOUT_SECTION_ID}`) return false;
  history.replaceState(null, '', `${location.pathname}${location.search}`);
  return true;
}

export function shouldCreatePaymentIntent(input: { isCreating: boolean; hasPayment: boolean }): boolean {
  return !input.isCreating && !input.hasPayment;
}

export function shouldShowStickyCheckoutCta(input: {
  hasPassedProductIntro: boolean;
  isFinalOfferVisible: boolean;
  isCheckoutEngaged: boolean;
}): boolean {
  return input.hasPassedProductIntro && !input.isFinalOfferVisible && !input.isCheckoutEngaged;
}

export function isTerminalCheckoutStatus(status: string): boolean {
  return ['paid', 'failed', 'canceled', 'refunded'].includes(status);
}

export function shouldResetCheckoutAttemptForApiError(code: unknown): boolean {
  return code === 'CHECKOUT_ATTEMPT_EXPIRED' || code === 'CHECKOUT_ATTEMPT_MISMATCH' || code === 'CHECKOUT_ATTEMPT_CONFLICT';
}

export function buildPaymentReturnUrl(origin: string, orderId: string): string {
  const url = new URL('/', origin);
  url.searchParams.set('payment_return', '1');
  url.searchParams.set('order', orderId);
  return url.toString();
}

export function getPaymentReturnOrderId(location: Pick<LocationLike, 'pathname' | 'search'>): string | null {
  if (location.pathname !== '/') return null;
  const params = new URLSearchParams(location.search);
  if (params.get('payment_return') !== '1') return null;
  return params.get('order') || null;
}

export function cleanPaymentReturnUrl(history: HistoryLike): void {
  history.replaceState(null, '', '/');
}
